import type { PostStatus } from '$lib/api/restApi';
import type { hubpermsPosts, User, Post } from '$lib/api/rpc';

export type PostHeaderParent =
	| 'collectionPage'
	| 'carousel'
	| 'postDetailPage'
	| 'manageContentPage'
	| 'searchPage';

interface GetOptionsMenuPermsArgs {
	type: PostHeaderParent;
	perms: hubpermsPosts;
	status: PostStatus;
	user?: User;
	post?: Post;
}

export interface OptionsMenuPerms {
	edit: boolean;
	settings: boolean;
	performance: boolean;
	feature: boolean;
	pin: boolean;
	hide: boolean;
	exportImages: boolean;
	exportComments: boolean;
	delete: boolean;
}

export const getOptionsMenuPerms = ({
	type,
	perms,
	status,
	user,
	post,
}: GetOptionsMenuPermsArgs): { renderOptionsMenu: boolean; permissions: OptionsMenuPerms } => {
	if (
		perms.manage_all &&
		post &&
		(post.type == 'widgetQuickPoll' || post.type == 'widgetLink' || post.type == 'widgetHtml') &&
		(type != 'manageContentPage' || status == 'published')
	) {
		return {
			renderOptionsMenu: true,
			permissions: {
				...defaultPermissions,
				edit: true,
				delete: true,
				pin: true,
				hide: true,
			},
		};
	}

	if (type == 'searchPage') return { renderOptionsMenu: false, permissions: defaultPermissions };
	let usersOwnPost = false;
	if (user && post) {
		usersOwnPost = user.id == post.user_id;
	}
	// member should be allowed to see/edit/delete their own draft or submitted posts
	if (type == 'manageContentPage' && (status == 'draft' || status == 'in-review') && usersOwnPost) {
		return {
			renderOptionsMenu: true,
			permissions: {
				...defaultPermissions,
				edit: true,
				delete: true,
			},
		};
	}

	if (!perms.manage_all) return { renderOptionsMenu: false, permissions: defaultPermissions };

	if (status == 'published' || status == 'hidden') {
		return {
			renderOptionsMenu: true,
			permissions: {
				edit: true,
				settings: true,
				performance: perms.view_metrics,
				feature: true,
				pin: type == 'collectionPage' || (type == 'manageContentPage' && status == 'published'),
				hide: true,
				exportImages: perms.download_contributions && type == 'postDetailPage',
				exportComments: perms.download_contributions && type == 'postDetailPage',
				delete: true,
			},
		};
	} else {
		return {
			renderOptionsMenu: true,
			permissions: {
				...defaultPermissions,
				edit: true,
				delete: true,
			},
		};
	}
};

const defaultPermissions = {
	settings: false,
	performance: false,
	feature: false,
	pin: false,
	hide: false,
	exportImages: false,
	exportComments: false,
	edit: false,
	delete: false,
};
